import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { scheduleUserDeletion } from 'src/redux/actions/User'

const MIN_DAYS = 2
const MAX_DAYS = 90
const DEFAULT_DAYS = 30

const ScheduleDeletionDialog = ({ open, setOpen, userId }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [days, setDays] = useState(DEFAULT_DAYS)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) setDays(DEFAULT_DAYS)
  }, [open])

  const clampDays = (value) => Math.min(MAX_DAYS, Math.max(MIN_DAYS, value))

  const deletionDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toLocaleDateString()

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await dispatch(scheduleUserDeletion(userId, days))
      toast.success(t('views.customerDataManagement.scheduleDeletionSuccess'))
      setOpen(false)
    } catch (error) {
      toast.error(t('views.customerDataManagement.scheduleDeletionError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Avatar sx={{ bgcolor: 'error.lighter', color: 'error.main' }}>
          <WarningAmberRoundedIcon />
        </Avatar>
        {t('views.customerDataManagement.scheduleDeletionTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            {t('views.customerDataManagement.scheduleDeletionWarning')}
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('views.customerDataManagement.scheduleDeletionDaysLabel')}
            </Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              <Slider
                value={days}
                min={MIN_DAYS}
                max={MAX_DAYS}
                onChange={(_, value) => setDays(value)}
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <TextField
                type="number"
                size="small"
                value={days}
                onChange={(e) => setDays(clampDays(Number(e.target.value) || MIN_DAYS))}
                inputProps={{ min: MIN_DAYS, max: MAX_DAYS }}
                sx={{ width: 90 }}
              />
            </Stack>
          </Box>

          <Typography variant="body2">
            {t('views.customerDataManagement.scheduleDeletionDatePreview')}{' '}
            <Typography component="span" variant="body2" fontWeight="600">
              {deletionDate}
            </Typography>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          {t('views.customerDataManagement.close')}
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={isSubmitting}>
          {t('views.customerDataManagement.scheduleDeletionConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ScheduleDeletionDialog
